import {
  EmbeddedStatus,
  IKnowledgeBaseItem,
  KBType,
} from 'features/chat/agent-chat-module/types/kb.service.type';
import { t } from 'i18next';
import type { IGetAgentModelResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { EMBEDDING_MODEL_LABELS, EMBEDDING_MODEL_ORDER } from './constants';

type TranslateFn = (key: string) => string;

export function getEmbeddingModelLabel(
  model: { model_name: string; model_name_label?: string },
  translate: TranslateFn = t
): string {
  const mapping = EMBEDDING_MODEL_LABELS[model.model_name];
  if (mapping) {
    return translate(mapping.labelKey) || mapping.defaultLabel;
  }

  return model.model_name_label || model.model_name;
}

export const getEmbeddingModelOrder = (modelName: string): number => {
  if (EMBEDDING_MODEL_ORDER[modelName] !== undefined) {
    return EMBEDDING_MODEL_ORDER[modelName];
  }
  const normalized = modelName.toLowerCase();
  if (normalized.includes('3-large') || normalized.includes('large')) return 1;
  if (normalized.includes('3-small') || normalized.includes('small')) return 2;
  if (normalized.includes('ada-002') || normalized.includes('ada')) return 3;
  return 99;
};

export const sortEmbeddingModels = <T extends { model_name: string }>(models: T[]): T[] => {
  return [...models].sort((a, b) => {
    const orderA = getEmbeddingModelOrder(a.model_name);
    const orderB = getEmbeddingModelOrder(b.model_name);
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.model_name.localeCompare(b.model_name);
  });
};

export const getDefaultEmbeddingModel = (models?: IGetAgentModelResponse) => {
  const embeddingModels = (models || []).filter((model) => model.model_type === 'embedding');
  const sorted = sortEmbeddingModels(embeddingModels);
  return sorted[0];
};

export const formatFullDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const isExternalWriteup = (item?: IKnowledgeBaseItem) =>
  Boolean(item?.meta_data?.ExternalSource?.system && item?.meta_data?.ExternalSource?.external_id);

export const getWriteupSourceUrl = (item: IKnowledgeBaseItem): string | undefined => {
  if (!isExternalWriteup(item)) return undefined;
  try {
    const url = new URL(item.meta_data.ExternalSource?.url || '');
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : undefined;
  } catch {
    return undefined;
  }
};

export const getKbName = (item: IKnowledgeBaseItem) => {
  if (isExternalWriteup(item) && item.meta_data.ExternalSource?.title) return item.meta_data.ExternalSource.title;
  switch (item.kb_type) {
    case KBType.FILE:
      return item.file?.name || t('UNTITLED_FILE', { defaultValue: 'Untitled file' });
    case KBType.TEXT:
      return item.text?.content?.slice(0, 60) || t('UNTITLED_NOTE', { defaultValue: 'Untitled note' });
    case KBType.LINK:
      return item.link?.url || t('UNTITLED_LINK', { defaultValue: 'Untitled link' });
    case KBType.QNA:
      return item.qa?.question || t('UNTITLED_QA', { defaultValue: 'Untitled Q&A' });
    default:
      return t('UNTITLED_KNOWLEDGE_ITEM', { defaultValue: 'Untitled knowledge item' });
  }
};

export const getKbTypeLabel = (value: KBType) => {
  switch (value) {
    case KBType.FILE:
      return t('FILES', { defaultValue: 'Files' });
    case KBType.TEXT:
      return t('WRITEUPS', { defaultValue: 'Writeups' });
    case KBType.LINK:
      return t('LINKS', { defaultValue: 'Links' });
    case KBType.QNA:
      return t('QNA', { defaultValue: 'Q&A' });
    default:
      return value;
  }
};

export const getEmbedBadgeVariant = (status?: EmbeddedStatus) => {
  switch (status) {
    case EmbeddedStatus.COMPLETED:
      return 'default';
    case EmbeddedStatus.FAILED:
      return 'destructive';
    case EmbeddedStatus.PROCESSING:
    case EmbeddedStatus.PENDING:
      return 'secondary';
    default:
      return 'outline';
  }
};

export const getEmbedStatusLabel = (
  status: EmbeddedStatus,
  translate: TranslateFn = t
) => {
  switch (status) {
    case EmbeddedStatus.PENDING:
      return translate('PENDING') || 'Pending';
    case EmbeddedStatus.COMPLETED:
      return translate('COMPLETED') || 'Completed';
    case EmbeddedStatus.PROCESSING:
      return translate('PROCESSING') || 'Processing';
    case EmbeddedStatus.FAILED:
      return translate('FAILED') || 'Failed';
  }
};
